import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from './AppIcon';
import { useScrollLock } from '../hooks/useScrollLock';

// Returns true if node or any descendant has a mappedPage
const hasMappedDescendant = (node) => {
  if (node.mappedPage !== null) return true;
  return (node.children || []).some(hasMappedDescendant);
};

// Collect all ancestor IDs of the active node
const getActiveAncestors = (nodes, activePage, ancestors = []) => {
  for (const node of nodes) {
    if (node.mappedPage === activePage) return [...ancestors, node.id];
    const found = getActiveAncestors(node.children || [], activePage, [...ancestors, node.id]);
    if (found) return found;
  }
  return null;
};

const MenuTreeItem = ({ node, depth, currentPage, expandedIds, onToggle, onNavigate }) => {
  const hasVisibleChildren = (node.children || []).some(hasMappedDescendant);
  const isActive = node.mappedPage === currentPage;
  const isExpanded = expandedIds.has(node.id);
  const isClickable = node.mappedPage !== null;

  return (
    <div>
      <div
        className={`flex items-center gap-2 py-2 pr-3 rounded-md cursor-pointer transition-colors
          ${isActive ? 'bg-accent/15 text-accent font-medium' : 'text-foreground hover:bg-muted'}
          ${!isClickable && !hasVisibleChildren ? 'opacity-50 cursor-default' : ''}`}
        style={{ paddingLeft: `${depth * 16 + 12}px` }}
        onClick={() => {
          if (isClickable) onNavigate(node.mappedPage);
          else if (hasVisibleChildren) onToggle(node.id);
        }}
      >
        {/* expand toggle */}
        {hasVisibleChildren ? (
          <button
            className="w-4 h-4 flex items-center justify-center flex-shrink-0 text-muted-foreground"
            onClick={(e) => { e.stopPropagation(); onToggle(node.id); }}
          >
            <Icon name={isExpanded ? 'ChevronDown' : 'ChevronRight'} size={13} />
          </button>
        ) : (
          <span className="w-4 flex-shrink-0" />
        )}

        <span className="flex-1 text-sm leading-snug">{node.name}</span>

        {isActive && <Icon name="MapPin" size={13} className="text-accent flex-shrink-0" />}
      </div>

      {hasVisibleChildren && isExpanded && (
        <div>
          {(node.children || [])
            .filter(hasMappedDescendant)
            .map((child) => (
              <MenuTreeItem
                key={child.id}
                node={child}
                depth={depth + 1}
                currentPage={currentPage}
                expandedIds={expandedIds}
                onToggle={onToggle}
                onNavigate={onNavigate}
              />
            ))}
        </div>
      )}
    </div>
  );
};

const CatalogMenuDrawer = ({ menuTree = [], currentPage, onNavigate, isOpen, onClose, embedMode = false }) => {
  const { t } = useTranslation();
  const [expandedIds, setExpandedIds] = useState(new Set());
  useScrollLock(isOpen);

  // Auto-expand ancestors of active node when opening
  useEffect(() => {
    if (!isOpen) return;
    const activePage = currentPage === 0 ? 1 : currentPage;
    const ancestors = getActiveAncestors(menuTree, activePage);
    if (ancestors?.length) setExpandedIds(new Set(ancestors));
  }, [isOpen, currentPage, menuTree]);

  // Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  const handleToggle = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleNavigate = (page) => {
    onNavigate(page);
    onClose();
  };

  const visibleTree = menuTree.filter(hasMappedDescendant);

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className={`fixed inset-0 bg-black/40 ${embedMode ? 'z-40' : 'z-40'}`}
          onClick={onClose}
        />
      )}

      {/* Drawer */}
      <div
        className={`fixed top-0 left-0 h-full bg-card border-r border-border shadow-elevation-xl
          transition-transform duration-250 ease-in-out flex flex-col
          ${embedMode ? 'z-50 w-64' : 'z-50 w-72'}
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border flex-shrink-0">
          <div className="flex items-center gap-2">
            <Icon name="Menu" size={18} className="text-foreground" />
            <span className="text-sm font-semibold text-foreground">{t('menuDrawer.title')}</span>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          >
            <Icon name="X" size={16} />
          </button>
        </div>

        {/* Tree */}
        <div className="flex-1 overflow-y-auto py-2 px-1">
          {visibleTree.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8 px-4">
              {t('menuDrawer.noMappings')}
            </p>
          ) : (
            visibleTree.map((node) => (
              <MenuTreeItem
                key={node.id}
                node={node}
                depth={0}
                currentPage={currentPage === 0 ? 1 : currentPage}
                expandedIds={expandedIds}
                onToggle={handleToggle}
                onNavigate={handleNavigate}
              />
            ))
          )}
        </div>
      </div>
    </>
  );
};

export { hasMappedDescendant };
export default CatalogMenuDrawer;
